import { Box, SxProps, Theme } from "@mui/material";

/**
 * Inline strokes lifted verbatim from `Contacts.dc.html`. They are drawn rather
 * than pulled from `@mui/icons-material` because the design's glyphs (stroke
 * width, cap style, proportions) do not exist in the Material set — swapping in
 * a lookalike would break the pixel match.
 *
 * Every icon takes its size and colour from the caller's `sx` so the screen
 * tokens stay the single source of those values.
 */

interface IconProps {
  sx?: SxProps<Theme>;
  /** Stroke weight — the design varies it per icon size. */
  strokeWidth?: number;
}

const svgBase = {
  flexShrink: 0,
  display: "block",
} as const;

const Svg = ({
  sx,
  strokeWidth = 2,
  children,
}: IconProps & { children: React.ReactNode }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    sx={[svgBase, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>}
  >
    {children}
  </Box>
);

/** Person with a "+" — "Add Contact" and the empty-state badge. */
export const PersonAddIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M19 8v6M22 11h-6" />
  </Svg>
);

/** Tray with an up arrow — "Bulk Upload". */
export const UploadIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M12 3v13M8 7l4-4 4 4" />
  </Svg>
);

/** Magnifier in the contact search field. */
export const SearchGlyph = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </Svg>
);

/** Descending bars with an arrow — the "Name" sort indicator. */
export const SortIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 6h11M3 12h7M3 18h5M17 8l3-3 3 3M20 5v14" />
  </Svg>
);

/** Chevron used by the dial-code control and the country select. */
export const ChevronDownIcon = ({ strokeWidth = 2.5, ...rest }: IconProps) => (
  <Svg strokeWidth={strokeWidth} {...rest}>
    <path d="M6 9l6 6 6-6" />
  </Svg>
);

/** Bin — "Delete Contact". */
export const TrashIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
  </Svg>
);

/** Left arrow — mobile "back to the contact list". */
export const BackIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M15 6l-6 6 6 6" />
  </Svg>
);

/** Right chevron — the phone row's "opens full screen" affordance. */
export const ChevronRightIcon = ({ strokeWidth = 2.2, ...rest }: IconProps) => (
  <Svg strokeWidth={strokeWidth} {...rest}>
    <path d="M9 6l6 6-6 6" />
  </Svg>
);

/** Circled "+" — the compact app bar's "Add contact" action. */
export const CirclePlusIcon = ({ strokeWidth = 2.2, ...rest }: IconProps) => (
  <Svg strokeWidth={strokeWidth} {...rest}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v8M8 12h8" />
  </Svg>
);
